class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.bundleRemovalInProgress = false;
    this.lineRemovalInProgress = false;
    this.quantityUpdateInProgress = false;
    this.upsellAddInProgress = false;
    this.checkoutInProgress = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Keep the listeners on the persistent outer custom element so they
    // survive cart data patches without accumulating handlers.
    this.addEventListener('click', this.handleDrawerOverlayClick.bind(this));
    this.addEventListener('click', this.handleCartRemoveClick.bind(this), true);
    this.addEventListener('submit', this.handleUpsellSubmit.bind(this), true);
    this.addEventListener('click', this.handleCheckoutClick.bind(this), true);

    this.rehydratePromise = null;
    this.pageShowHandler = this.handlePageShow.bind(this);
    this.pageHideHandler = this.handlePageHide.bind(this);

    window.addEventListener('pageshow', this.pageShowHandler);
    window.addEventListener('pagehide', this.pageHideHandler);

    this.setHeaderCartIconAccessibility();
  }

  getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  resetCheckoutButton() {
    this.checkoutInProgress = false;

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (!checkoutButton) return;

    checkoutButton.removeAttribute('disabled');
    checkoutButton.removeAttribute('aria-disabled');
    checkoutButton.removeAttribute('aria-busy');
    checkoutButton.classList.remove('loading');
  }

  resetTransientUI() {
    this.checkoutInProgress = false;
    this.lineRemovalInProgress = false;
    this.quantityUpdateInProgress = false;
    this.upsellAddInProgress = false;
    this.bundleRemovalInProgress = false;

    // These flags can be restored by BFCache even though their original
    // network requests no longer exist. Bundle-page/global cleanup owns
    // __bundleCartMutation, so do not overwrite that flag here.
    window.__cartKeyRemovalInProgress = false;
    window.__cartQuantityMutationInProgress = false;
    window.__cartUpsellAddInProgress = false;
    window.__bundleRemovalInProgress = false;
    window.__freeGiftMutationInProgress = false;
    window.__freeGiftSyncPending = false;

    this.removeAttribute('aria-busy');
    document.documentElement.classList.remove('cart-aux-syncing');

    this.querySelectorAll('.loading__spinner').forEach((spinner) => {
      spinner.classList.add('hidden');
    });

    this.querySelectorAll('cart-remove-button button').forEach((button) => {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    });

    this.querySelectorAll('.cart-item--removing').forEach((row) => {
      row.classList.remove('cart-item--removing');
    });

    this.resetCheckoutButton();

    if (!this.classList.contains('active')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  handlePageHide() {
    // Keep BFCache from storing the checkout button in its temporary
    // disabled/loading state. Do not touch active cart mutation flags here.
    this.checkoutInProgress = false;
    this.resetCheckoutButton();
  }

  handlePageShow(event) {
    const navigation = performance.getEntriesByType('navigation')[0];
    const isHistoryRestore = Boolean(event?.persisted || navigation?.type === 'back_forward');

    this.resetTransientUI();

    if (!isHistoryRestore) return;

    // Run after all pageshow handlers. Bundle cleanup can start during the
    // same event; rehydration will then wait for that mutation to finish.
    setTimeout(() => {
      this.rehydrateFromServer();
    }, 0);
  }

  async rehydrateFromServer() {
    if (this.rehydratePromise) return this.rehydratePromise;

    this.rehydratePromise = (async () => {
      try {
        await this.waitForCartIdle(8000);

        if (typeof window.__ensureFreeGiftSynced === 'function') {
          try {
            await window.__ensureFreeGiftSynced();
          } catch (error) {
            console.error('[Cart Drawer] Free gift restore sync failed:', error);
          }
        }

        await this.waitForCartIdle(8000);

        const cart = await this.getCart();
        const sections = await this.getFreshCartSections('cart_restore');

        this.renderContents({
          sections,
          id: null,
          openDrawer: false,
          item_count: Number(cart.item_count || 0)
        });
      } catch (error) {
        console.error('[Cart Drawer] Restore refresh failed:', error);
      } finally {
        this.rehydratePromise = null;
        this.resetCheckoutButton();

        if (!this.cartMutationBusy()) {
          document.documentElement.classList.remove('cart-aux-syncing');
        }
      }
    })();

    return this.rehydratePromise;
  }

  isBundleItem(item) {
    if (!item || !item.properties) return false;

    const value = item.properties._bundle;
    return value === true || String(value ?? '').toLowerCase() === 'true' || String(value ?? '') === '1';
  }

  async getCart() {
    const response = await fetch(this.getRoot() + 'cart.js?_=' + Date.now(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart: ' + response.status);
    }

    return response.json();
  }

  async clearDiscountAfterBundleRemoval() {
    const formData = new FormData();
    formData.append('discount', '');

    const response = await fetch(
      this.getRoot() + 'cart/update.js?bundle_remove=1&_=' + Date.now(),
      {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error('Unable to remove bundle discount: ' + response.status);
    }

    return response.json();
  }

  cartMutationBusy() {
    return Boolean(
      window.__bundleCartMutation ||
      window.__bundleRemovalInProgress ||
      window.__freeGiftMutationInProgress ||
      window.__freeGiftSyncPending ||
      window.__cartKeyRemovalInProgress ||
      window.__cartQuantityMutationInProgress ||
      window.__cartUpsellAddInProgress
    );
  }

  setCartInteractionLock(active) {
    if (active) {
      document.documentElement.classList.add('cart-aux-syncing');
      return;
    }

    if (!this.cartMutationBusy()) {
      document.documentElement.classList.remove('cart-aux-syncing');
    }
  }

  handleDrawerOverlayClick(event) {
    if (event.target?.id === 'CartDrawer-Overlay') {
      this.close();
    }
  }

  setRemoveLoading(removeElement, active) {
    if (!removeElement) return;

    const row = removeElement.closest('.cart-item');
    const spinner = row?.querySelector('.loading__spinner');
    const button = removeElement.querySelector('button');

    if (row) row.classList.toggle('cart-item--removing', active);
    if (spinner) spinner.classList.toggle('hidden', !active);

    if (button) {
      button.toggleAttribute('disabled', active);
      button.setAttribute('aria-busy', active ? 'true' : 'false');
    }
  }

  async waitForCartIdle(timeout = 5000) {
    const started = Date.now();

    while (this.cartMutationBusy()) {
      if (Date.now() - started >= timeout) break;
      await new Promise((resolve) => setTimeout(resolve, 40));
    }
  }

  handleUpsellSubmit(event) {
    const form = event.target.closest('form[data-cart-upsell-form="true"]');

    if (!form || !this.contains(form)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.upsellAddInProgress || this.cartMutationBusy()) return;

    this.addUpsellFromForm(form);
  }

  setUpsellFormLoading(form, active) {
    if (!form) return;

    const button = form.querySelector('[type="submit"]');
    const spinner = form.querySelector('.loading__spinner');

    if (button) {
      button.classList.toggle('loading', active);
      button.toggleAttribute('disabled', active);
      button.setAttribute('aria-disabled', active ? 'true' : 'false');
      button.setAttribute('aria-busy', active ? 'true' : 'false');
    }

    if (spinner) {
      spinner.classList.toggle('hidden', !active);
    }
  }

  async addUpsellFromForm(form) {
    if (this.upsellAddInProgress || this.cartMutationBusy()) return;

    this.upsellAddInProgress = true;
    window.__cartUpsellAddInProgress = true;
    this.setCartInteractionLock(true);
    this.setAttribute('aria-busy', 'true');
    this.setUpsellFormLoading(form, true);

    try {
      const formData = new FormData(form);

      formData.set(
        'sections',
        this.getSectionsToRender().map((section) => section.id).join(',')
      );

      formData.set(
        'sections_url',
        window.location.pathname + window.location.search
      );

      this.setActiveElement(document.activeElement);

      const response = await fetch(
        this.getRoot() + 'cart/add.js?drawer_upsell=1&_=' + Date.now(),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData,
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      const cartResponse = await response.json().catch(() => null);

      if (!response.ok || !cartResponse || cartResponse.status) {
        const message =
          cartResponse?.description ||
          cartResponse?.message ||
          ('Upsell add failed: ' + response.status);

        throw new Error(message);
      }

      let sections = cartResponse.sections;

      if (!sections) {
        sections = await this.getFreshCartSections('drawer_upsell');
      }

      if (sections) {
        this.renderContents({
          sections,
          id: cartResponse.id || null,
          openDrawer: true
        });
      }

      window.dispatchEvent(
        new CustomEvent('cart:mutation-complete', {
          detail: {
            action: 'upsell-added',
            variantId: formData.get('id')
          }
        })
      );
    } catch (error) {
      console.error('[Cart Drawer] Upsell add failed:', error);

      const wrapper = form.closest('.cart-upsell-product-form');
      const errorWrapper = wrapper?.querySelector(
        '.product-form__error-message-wrapper'
      );
      const errorText = errorWrapper?.querySelector(
        '.product-form__error-message'
      );

      if (errorText) {
        errorText.textContent =
          error?.message ||
          window.cartStrings?.error ||
          'Unable to add this product.';
      }

      if (errorWrapper) {
        errorWrapper.hidden = false;
      }
    } finally {
      window.__cartUpsellAddInProgress = false;
      this.upsellAddInProgress = false;
      this.removeAttribute('aria-busy');
      this.setCartInteractionLock(false);

      if (form.isConnected) {
        this.setUpsellFormLoading(form, false);
      }
    }
  }

  handleCheckoutClick(event) {
    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton || !this.contains(checkoutButton)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.checkoutInProgress) return;
    this.checkoutWithCartSync(checkoutButton);
  }

  async checkoutWithCartSync(checkoutButton) {
    this.checkoutInProgress = true;

    checkoutButton.setAttribute('disabled', 'disabled');
    checkoutButton.setAttribute('aria-disabled', 'true');
    checkoutButton.setAttribute('aria-busy', 'true');
    checkoutButton.classList.add('loading');

    try {
      await this.waitForCartIdle();

      if (typeof window.__ensureFreeGiftSynced === 'function') {
        await window.__ensureFreeGiftSynced();
      }

      await this.waitForCartIdle();
      window.location.assign('/checkout');
    } catch (error) {
      console.error('[Cart Drawer] Checkout sync failed:', error);

      checkoutButton.removeAttribute('disabled');
      checkoutButton.removeAttribute('aria-disabled');
      checkoutButton.removeAttribute('aria-busy');
      checkoutButton.classList.remove('loading');
      this.checkoutInProgress = false;

      const errorBox = document.getElementById('CartDrawer-CartErrors');
      if (errorBox) {
        errorBox.textContent = window.cartStrings?.error || 'Unable to update the cart. Please try again.';
      }
    }
  }

  handleCartRemoveClick(event) {
    const removeElement = event.target.closest('cart-remove-button[data-line-key]');

    if (!removeElement || !this.contains(removeElement)) return;

    // Never let Dawn/cart.js send a line-number based remove request from the
    // drawer. Cart line numbers can change when the free gift or a bundle is
    // added/removed. The Shopify line-item key remains stable.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.bundleRemovalInProgress || this.lineRemovalInProgress || this.cartMutationBusy()) {
      return;
    }

    if (removeElement.dataset.bundleItem === 'true') {
      this.removeBundleItems(removeElement);
      return;
    }

    this.removeLineItemByKey(removeElement);
  }

  async removeLineItemByKey(removeElement) {
    if (this.lineRemovalInProgress || this.cartMutationBusy()) return;

    const lineKey = removeElement?.dataset?.lineKey;
    if (!lineKey) return;

    this.lineRemovalInProgress = true;
    window.__cartKeyRemovalInProgress = true;
    this.setCartInteractionLock(true);
    this.setAttribute('aria-busy', 'true');

    const removeButton = removeElement.querySelector('button');
    this.setRemoveLoading(removeElement, true);

    try {
      const response = await fetch(
        this.getRoot() + 'cart/change.js?keyed_remove=1&_=' + Date.now(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname + window.location.search
          }),
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      const cart = await response.json().catch(() => null);

      if (!response.ok || !cart) {
        const message = cart?.description || cart?.message || ('Cart remove failed: ' + response.status);
        throw new Error(message);
      }

      let sections = cart.sections;

      if (!sections) {
        sections = await this.getFreshCartSections('keyed_remove');
      }

      if (sections) {
        this.renderContents({
          sections,
          id: null,
          openDrawer: true,
          item_count: Number(cart.item_count || 0)
        });
      }

      window.dispatchEvent(
        new CustomEvent('cart:mutation-complete', {
          detail: {
            action: 'removed',
            key: lineKey,
            cart
          }
        })
      );
    } catch (error) {
      console.error('[Cart Drawer] Key remove failed:', error);

      this.setRemoveLoading(removeElement, false);

      const errorBox = document.getElementById('CartDrawer-CartErrors');
      if (errorBox) {
        errorBox.textContent = window.cartStrings?.error || 'Unable to update the cart. Please try again.';
      }
    } finally {
      this.removeAttribute('aria-busy');
      window.__cartKeyRemovalInProgress = false;
      this.lineRemovalInProgress = false;
      this.setCartInteractionLock(false);
      if (removeElement?.isConnected) this.setRemoveLoading(removeElement, false);
    }
  }

  async getFreshCartSections(marker = 'bundle_remove') {
    const response = await fetch(
      this.getRoot() + '?sections=cart-drawer,cart-icon-bubble&' + encodeURIComponent(marker) + '=1&_=' + Date.now(),
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error('Unable to refresh cart drawer: ' + response.status);
    }

    return response.json();
  }

  async removeBundleItems(removeElement) {
    if (this.bundleRemovalInProgress || this.cartMutationBusy()) return;

    this.bundleRemovalInProgress = true;
    window.__bundleRemovalInProgress = true;
    window.__bundleCartMutation = true;
    this.setCartInteractionLock(true);

    this.setAttribute('aria-busy', 'true');

    const removeButton = removeElement?.querySelector('button');
    this.setRemoveLoading(removeElement, true);

    try {
      const cart = await this.getCart();
      const bundleItems = Array.isArray(cart.items)
        ? cart.items.filter((item) => this.isBundleItem(item))
        : [];

      if (!bundleItems.length) return;

      const updates = {};
      bundleItems.forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });

      const updateResponse = await fetch(
        this.getRoot() + 'cart/update.js?bundle_remove=1&_=' + Date.now(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ updates }),
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!updateResponse.ok) {
        throw new Error('Unable to remove bundle items: ' + updateResponse.status);
      }

      await updateResponse.json();
      await this.clearDiscountAfterBundleRemoval();

      const finalCart = await this.getCart();
      const sections = await this.getFreshCartSections('bundle_remove');

      try { sessionStorage.removeItem('custom__cart_active'); } catch (_) {}
      try { localStorage.removeItem('custom__bundle_cart_active'); } catch (_) {}

      this.renderContents({
        sections,
        id: null,
        openDrawer: true,
        item_count: Number(finalCart.item_count || 0)
      });

      window.dispatchEvent(
        new CustomEvent('bundle-cart:changed', {
          detail: {
            action: 'removed',
            cart: finalCart
          }
        })
      );

      window.dispatchEvent(
        new CustomEvent('cart:mutation-complete', {
          detail: {
            action: 'bundle-removed',
            cart: finalCart
          }
        })
      );
    } catch (error) {
      console.error('[Bundle Cart] Remove failed:', error);

      this.setRemoveLoading(removeElement, false);
    } finally {
      this.removeAttribute('aria-busy');
      window.__bundleRemovalInProgress = false;
      window.__bundleCartMutation = false;
      this.bundleRemovalInProgress = false;
      this.setCartInteractionLock(false);
      if (removeElement?.isConnected) this.setRemoveLoading(removeElement, false);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) return;

    const drawerHTML = parsedState.sections['cart-drawer'];
    if (!drawerHTML) return;

    const wasOpen = this.classList.contains('active');
    const shouldOpen = parsedState.openDrawer !== false;

    this.productId = parsedState.id;

    const parsedDocument = new DOMParser().parseFromString(drawerHTML, 'text/html');
    const freshDrawer = parsedDocument.querySelector('#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');

    if (!freshDrawer || !currentDrawer) return;

    const currentEmpty = !currentDrawer.querySelector('#CartDrawer-Checkout');
    const freshEmpty = !freshDrawer.querySelector('#CartDrawer-Checkout');

    // Full replacement is reserved for the rare empty <-> non-empty structural
    // transition. Normal cart updates patch only the data that changed.
    if (currentEmpty !== freshEmpty) {
      currentDrawer.innerHTML = freshDrawer.innerHTML;
    } else if (freshEmpty) {
      this.patchNodeInnerHTML(
        currentDrawer.querySelector('.drawer__inner-empty'),
        freshDrawer.querySelector('.drawer__inner-empty')
      );
    } else {
      this.patchNodeInnerHTML(
        currentDrawer.querySelector('#CartDrawer-CartItems'),
        freshDrawer.querySelector('#CartDrawer-CartItems')
      );

      this.patchOptionalNode(
        currentDrawer,
        freshDrawer,
        'custom-free-shipping'
      );

      this.patchNodeInnerHTML(
        currentDrawer.querySelector('.drawer__inner > .drawer__footer'),
        freshDrawer.querySelector('.drawer__inner > .drawer__footer')
      );

      this.patchPersistentUpsell(currentDrawer, freshDrawer);
    }

    this.patchCartBubble(parsedState.sections['cart-icon-bubble']);

    const isEmpty =
      typeof parsedState.item_count === 'number'
        ? parsedState.item_count === 0
        : freshEmpty;

    this.classList.toggle('is-empty', isEmpty);

    const drawerInner = this.querySelector('.drawer__inner');
    const drawerItems = this.querySelector('cart-drawer-items');

    if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');

    if (checkoutButton && !this.checkoutInProgress) {
      checkoutButton.removeAttribute('disabled');
      checkoutButton.removeAttribute('aria-disabled');
      checkoutButton.removeAttribute('aria-busy');
      checkoutButton.classList.remove('loading');
    }

    if (wasOpen) {
      document.body.classList.add('overflow-hidden');
    } else if (shouldOpen) {
      this.open();
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  patchNodeInnerHTML(currentNode, freshNode) {
    if (!currentNode || !freshNode) return false;
    if (currentNode.innerHTML === freshNode.innerHTML) return false;

    currentNode.innerHTML = freshNode.innerHTML;
    return true;
  }

  patchOptionalNode(currentRoot, freshRoot, selector) {
    const currentNode = currentRoot.querySelector(selector);
    const freshNode = freshRoot.querySelector(selector);

    if (currentNode && freshNode) {
      currentNode.style.removeProperty('display');
      this.patchNodeInnerHTML(currentNode, freshNode);
      return;
    }

    if (currentNode && !freshNode) {
      currentNode.style.display = 'none';
      return;
    }

    if (!currentNode && freshNode) {
      const drawerInner = currentRoot.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.insertBefore(
          freshNode.cloneNode(true),
          drawerInner.querySelector('cart-drawer-items')
        );
      }
    }
  }

  patchPersistentUpsell(currentRoot, freshRoot) {
    const currentUpsell = currentRoot.querySelector('#CartDrawer-Upsell');
    const freshUpsell = freshRoot.querySelector('#CartDrawer-Upsell');

    if (!currentUpsell && !freshUpsell) return;

    if (currentUpsell && !freshUpsell) {
      currentUpsell.style.display = 'none';
      return;
    }

    if (!currentUpsell && freshUpsell) {
      const drawerItems = currentRoot.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.appendChild(freshUpsell.cloneNode(true));
      return;
    }

    currentUpsell.style.removeProperty('display');

    const currentWrapper = currentUpsell.querySelector('.complementary-products-wrapper');
    const freshWrapper = freshUpsell.querySelector('.complementary-products-wrapper');

    if (currentWrapper && freshWrapper) {
      ['data-cart-total-ml', 'data-recommended-ml'].forEach((name) => {
        if (freshWrapper.hasAttribute(name)) {
          currentWrapper.setAttribute(name, freshWrapper.getAttribute(name));
        } else {
          currentWrapper.removeAttribute(name);
        }
      });
    }

    const currentHeader = currentUpsell.querySelector('.complementary-products-header');
    const freshHeader = freshUpsell.querySelector('.complementary-products-header');

    if (currentHeader && freshHeader && currentHeader.innerHTML !== freshHeader.innerHTML) {
      currentHeader.innerHTML = freshHeader.innerHTML;
    }

    const currentOuter = currentUpsell.querySelector('.complementary-slider-outer');
    const freshOuter = freshUpsell.querySelector('.complementary-slider-outer');

    if (!currentOuter || !freshOuter) return;

    const sliderShouldShow = freshOuter.dataset.upsellSliderActive !== 'false';
    currentOuter.style.display = sliderShouldShow ? '' : 'none';

    const currentSlider = currentOuter.querySelector('.complementary-products-slider-drawer');
    const freshSlider = freshOuter.querySelector('.complementary-products-slider-drawer');

    if (!currentSlider || !freshSlider) return;

    const currentSlides = Array.from(
      currentSlider.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );
    const freshSlides = Array.from(
      freshSlider.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );

    const signature = (slides) =>
      slides.map((slide) => {
        const card = slide.querySelector('.complementary-product-card');
        return `${card?.dataset?.productId || ''}:${card?.dataset?.variantId || ''}`;
      }).join('|');

    if (signature(currentSlides) === signature(freshSlides)) return;

    const swiper = currentSlider.swiper;
    const slideHTML = freshSlides.map((slide) => slide.outerHTML);

    // Preferred path: update slides through the already-mounted Swiper.
    if (
      swiper &&
      typeof swiper.removeAllSlides === 'function' &&
      typeof swiper.appendSlide === 'function'
    ) {
      const activeIndex = Math.min(
        Number(swiper.realIndex || swiper.activeIndex || 0),
        Math.max(slideHTML.length - 1, 0)
      );

      swiper.removeAllSlides();
      if (slideHTML.length) swiper.appendSlide(slideHTML);
      swiper.update();

      if (slideHTML.length && typeof swiper.slideTo === 'function') {
        swiper.slideTo(activeIndex, 0);
      }
      return;
    }

    // Fallback: keep the outer custom element mounted and replace slide nodes
    // only. This avoids reconnecting complementary-slider-drawer.
    const currentSlidesWrapper = currentSlider.querySelector('.swiper-wrapper');

    if (currentSlidesWrapper) {
      currentSlidesWrapper.replaceChildren(
        ...freshSlides.map((slide) => slide.cloneNode(true))
      );

      if (swiper && typeof swiper.update === 'function') swiper.update();
    }
  }

  patchCartBubble(sectionHTML) {
    if (!sectionHTML) return;

    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const freshBubble = doc.getElementById('cart-icon-bubble');
    const freshSection = doc.querySelector('.shopify-section');
    const html = freshBubble?.innerHTML ?? freshSection?.innerHTML;

    if (html != null && target.innerHTML !== html) {
      target.innerHTML = html;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  async updateQuantity(line, quantity, name, variantId) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (
      !cartDrawer ||
      this.quantityUpdateInProgress ||
      cartDrawer.cartMutationBusy?.()
    ) {
      return;
    }

    const input =
      this.querySelector(`#Drawer-quantity-${line}`) ||
      this.querySelector(`.quantity__input[data-index="${line}"]`);

    const lineKey = input?.dataset?.lineKey;

    if (!lineKey) {
      console.error('[Cart Drawer] Missing line-item key for quantity update.');
      return;
    }

    this.quantityUpdateInProgress = true;
    cartDrawer.quantityUpdateInProgress = true;
    window.__cartQuantityMutationInProgress = true;

    cartDrawer.setCartInteractionLock(true);
    cartDrawer.setAttribute('aria-busy', 'true');

    if (typeof this.enableLoading === 'function') {
      this.enableLoading(line);
    } else {
      this.classList.add('cart__items--disabled');
    }

    try {
      const response = await fetch(
        (window.Shopify?.routes?.root || '/') +
          'cart/change.js?keyed_quantity=1&_=' +
          Date.now(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: Number(quantity),
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname + window.location.search
          }),
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      const cart = await response.json().catch(() => null);

      if (!response.ok || !cart) {
        throw new Error(
          cart?.description ||
          cart?.message ||
          `Cart quantity update failed: ${response.status}`
        );
      }

      cartDrawer.renderContents({
        sections: cart.sections,
        id: variantId || null,
        openDrawer: false,
        item_count: Number(cart.item_count || 0)
      });

      window.dispatchEvent(
        new CustomEvent('cart:mutation-complete', {
          detail: {
            action: 'quantity-changed',
            key: lineKey,
            quantity: Number(quantity),
            cart
          }
        })
      );
    } catch (error) {
      console.error('[Cart Drawer] Quantity update failed:', error);

      const errorBox = document.getElementById('CartDrawer-CartErrors');
      if (errorBox) {
        errorBox.textContent =
          error?.message ||
          window.cartStrings?.error ||
          'Unable to update the cart. Please try again.';
      }
    } finally {
      window.__cartQuantityMutationInProgress = false;
      cartDrawer.quantityUpdateInProgress = false;
      this.quantityUpdateInProgress = false;

      cartDrawer.removeAttribute('aria-busy');
      cartDrawer.setCartInteractionLock(false);

      if (this.isConnected) {
        if (typeof this.disableLoading === 'function') {
          this.disableLoading(line);
        } else {
          this.classList.remove('cart__items--disabled');
        }
      }
    }
  }

  getSectionsToRender() {
    // Compatibility only. Drawer quantity updates are handled above and never
    // replace .drawer__inner.
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

   (function () {
    var discountcode = `Black Friday Sale`;
    const CODE = (window.DISCOUNT_CODE || discountcode).trim();
    const POLL_TRIES = 6;
    const POLL_INTERVAL_MS = 250;

    if (!CODE) return;
    const getCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    const codeInCart = (cart, code) => {
      if (!cart || !Array.isArray(cart.discount_codes)) return false;
      const want = code.toLowerCase();
      for (const dc of cart.discount_codes) {
        if (!dc) continue;
        const name = String(dc.code || '')
          .trim()
          .toLowerCase();
        const applied =
          dc.applied === true ||
          dc.applicable === true ||
          (typeof dc.applied === 'undefined' && typeof dc.applicable === 'undefined');
        if (name === want && applied) return true;
      }
      return false;
    };
    const applyDiscount = (code) =>
      fetch('/discount/' + encodeURIComponent(code) + '?ajax=1', { credentials: 'same-origin' }).catch(() => {
      });

    const pollForCode = (triesLeft, onOk, onFail) => {
      getCart()
        .then((cart) => {
          if (codeInCart(cart, CODE)) onOk();
          else if (triesLeft > 0) setTimeout(() => pollForCode(triesLeft - 1, onOk, onFail), POLL_INTERVAL_MS);
          else onFail();
        })
        .catch(onFail);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    function start() {
      getCart()
        .then((cart) => {
          if (codeInCart(cart, CODE)) return;
          applyDiscount(CODE).finally(() => {
            pollForCode(
              POLL_TRIES,
              () => {
              },
              () => {
                console.warn('Discount not applied:', CODE);
              }
            );
          });
        })
        .catch(() => {
          console.warn('Could not read cart to check discount.');
        });
    }
  })();
 
// $(function(){
//  initDiscountForm();
// });

// document.addEventListener('initDiscountForm', () => {
//   initDiscountForm();  
// })


// function initDiscountForm(){


  


//   $('.discount-msg').remove();
//    var $box=$('.discount-box'),
//       $maincartdrawer=$('.cart-drawer'),
//       $form=$('#discountForm'),
//       $field=$('.discount-box__field'),
//       $input=$('#discount_code'),
//       $button=$form.find('.discount-box__btn'),
//       $buttonparent=$form.find('.button-container'),
//       $msg=$('<div class="discount-msg"></div>').insertAfter($form),
//       $cartDrawer=document.querySelector('cart-drawer'),
//       LS_KEY='lastAppliedDiscountCode';

//   function refreshSelectors(){
//     $box=$('.discount-box');
//     $maincartdrawer=$('.cart-drawer'),
//     $form=$('#discountForm');
//     $field=$('.discount-box__field');
//     $input=$('#discount_code');
//     $button=$form.find('.discount-box__btn');
//     $msg=$('.discount-msg');
//     if(!$msg.length){ $msg=$('<div class="discount-msg"></div>').insertAfter($form); }
//   }

//   function getCartSections(){
//     return $.ajax({url:'/?sections=cart-drawer,cart-icon-bubble',dataType:'json'});
//   }
//   function getCartJSON(){ return $.getJSON('/cart.js'); }

//   function codeInCart(cart, code){
//     if(!cart||!code) return false;
//     var want=String(code).trim().toLowerCase();

//     if(Array.isArray(cart.discount_codes)){
//       for(var i=0;i<cart.discount_codes.length;i++){
//         var dc=cart.discount_codes[i];
//         if(!dc) continue;
//         var name=String(dc.code||'').trim().toLowerCase();
//         var appliedFlag=(dc.applied===true)||(dc.applicable===true)||(typeof dc.applied==='undefined'&&typeof dc.applicable==='undefined');
//         if(name===want && appliedFlag) return true;
//       }
//     }

//     if(Array.isArray(cart.items)){
//       for(var j=0;j<cart.items.length;j++){
//         var it=cart.items[j];
//         var allocs = [].concat(it.line_level_discount_allocations||[], it.discount_allocations||[]);
//         for(var k=0;k<allocs.length;k++){
//           var da=(allocs[k]&&allocs[k].discount_application)||allocs[k];
//           if(da && da.type==='discount_code'){
//             var title=String(da.title||'').trim().toLowerCase();
//             if(title===want) return true;
//           }
//         }
//       }
//     }
//     return false;
//   }

//   function firstAppliedCode(cart){
//     if(cart && Array.isArray(cart.discount_codes) && cart.discount_codes.length){
//       var dc=cart.discount_codes.find(function(d){return d&&(d.applied===true||d.applicable===true||typeof d.applied==='undefined');});
//       if(dc && dc.code) return dc.code;
//     }
//     if(Array.isArray(cart.items)){
//       for(var j=0;j<cart.items.length;j++){
//         var it=cart.items[j];
//         var allocs=[].concat(it.line_level_discount_allocations||[], it.discount_allocations||[]);
//         var hit=allocs.find(function(a){var da=(a&&a.discount_application)||a; return da&&da.type==='discount_code'&&da.title;});
//         if(hit){ var da=(hit.discount_application)||hit; return da.title||''; }
//       }
//     }
//     return '';
//   }

//   function setError(text,keepValue){
//     refreshSelectors();
//     if(keepValue!=null) $input.val(keepValue);
//     $field.addClass('is-error');
//     $msg.text(text||'Der Rabattcode ist ungültig').show();
//     $button.removeClass('is-success loading').text('Anwenden');
//     $box.removeClass('locked');
//     $input.prop('disabled',false);
//     localStorage.removeItem(LS_KEY);
//     $maincartdrawer.removeClass('coupon-applied');
//     $buttonparent.removeClass('has-success');
//   }

//   function setApplied(code){
//     refreshSelectors();
//     $input.val((code||'').toUpperCase());
//     $field.removeClass('is-error');
//     $msg.hide();
//     $maincartdrawer.addClass('coupon-applied');
//     $buttonparent.addClass('has-success');
//     $button.addClass('is-success').removeClass('loading').html(`
//     <span class="success-discount-btn">
//     <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;">
//       <path d="M1.66634 7.0026C1.66634 4.05708 4.05415 1.66927 6.99967 1.66927C9.94521 1.66927 12.333 4.05708 12.333 7.0026C12.333 9.94814 9.94521 12.3359 6.99967 12.3359C4.05415 12.3359 1.66634 9.94814 1.66634 7.0026ZM6.99967 0.335938C3.31777 0.335938 0.333008 3.3207 0.333008 7.0026C0.333008 10.6845 3.31777 13.6693 6.99967 13.6693C10.6815 13.6693 13.6663 10.6845 13.6663 7.0026C13.6663 3.3207 10.6815 0.335938 6.99967 0.335938ZM10.6377 5.30734L9.69494 4.36453L6.33301 7.72647L4.47108 5.86454L3.52827 6.80734L6.33301 9.61207L10.6377 5.30734Z" fill="#77FFA4"/>
//     </svg>
//     <span>Rabatt aktiviert!</span>
//   `);

//     $box.addClass('locked');
//     $input.prop('disabled',true);
//     if(code) localStorage.setItem(LS_KEY,code);
//     $("#remove_code").show();
//   }

//   function clearApplied(){
//     refreshSelectors();
//     $input.val('');
//     $field.removeClass('is-error');
//     $msg.hide();
//     $button.removeClass('is-success loading').text('Anwenden');
//     $maincartdrawer.removeClass('coupon-applied');
//     $box.removeClass('locked');
//     $input.prop('disabled',false);
//     localStorage.removeItem(LS_KEY);
//     $buttonparent.removeClass('has-success');
//     $("#remove_code").show();
//   }

       

//      document.getElementById('remove_code').addEventListener('click', function () {
//       const formData = new FormData();
//       formData.append('discount', '');

//       fetch(`${window.Shopify.routes.root}cart/update.js`, {
//         method: 'POST',
//         body: formData
//       })
//       .then(response => response.json())
//       .then(data => {
//         getCartJSON().then(function(cart) {
//           getCartSections().then(function(sectionData) {
//             if ($cartDrawer && typeof $cartDrawer.renderContents === 'function') {
//               $cartDrawer.renderContents({ sections: sectionData, id: null });
//             }
            
//             initDiscountForm();
//           });
//         });
//       })
//       .catch(error => {
//         console.error('Error removing discount:', error);
//       });
//       clearApplied();
//       this.style.display = 'none';
//     });



//   $form.on('submit', function(e){
//       e.preventDefault();
//       var code = $.trim($input.val());
//       if(!code){ 
//         setError('Bitte gib einen Rabattcode ein.', code);
//         return;
//       }

//       $button.addClass('loading').prop('disabled', true).attr('aria-busy','true');
//       const formData = new FormData();
//       formData.append('discount', '');

//       fetch(`${window.Shopify.routes.root}cart/update.js`, {
//         method: 'POST',
//         body: formData
//       })
//       .then(response => response.json())
//       .then(data => {
//           getCartJSON().then(function(cart){
//             var currentCode = firstAppliedCode(cart);
//             if(currentCode && currentCode.toLowerCase() !== code.toLowerCase()){
              
//                 return applyDiscount(code);
              
//             } else {
//               return applyDiscount(code);
//             }
//           });
//       });
//     });

//     function applyDiscount(code){
//       return $.get('/discount/' + encodeURIComponent(code) + '?ajax=1')
//         .always(function(){
//           getCartJSON().then(function(cart){
//             if(!codeInCart(cart, code)){
//               setError('Der Rabattcode ist ungültig', code);
//               $button.prop('disabled', false).removeAttr('aria-busy');
//               return;
//             }
//             getCartSections().then(function(sectionData){
//               if($cartDrawer && typeof $cartDrawer.renderContents === 'function'){
//                 $cartDrawer.renderContents({ sections: sectionData, id: null });
//               }
//               setTimeout(function(){ setApplied(firstAppliedCode(cart) || code); }, 200);
//               document.getElementById('remove_code').addEventListener('click', function() {
//            clearApplied();
//         });
//             }).always(function(){
//               $button.prop('disabled', false).removeAttr('aria-busy');
//             });
//           });
//         });
//     }


//   $input.on('input change',function(){
//     refreshSelectors();
//     $field.removeClass('is-error');
//     $msg.hide();
//     console.log('hhkj '+ $input.val())
//    if( $input.val() != ''){
//     $("#remove_code").show();
//    }
//    else{
//     $("#remove_code").hide();
//    }
//   });

//   var savedCode=localStorage.getItem(LS_KEY);
//   if(savedCode){
//     getCartJSON().then(function(cart){
//       if(codeInCart(cart,savedCode)){ setApplied(savedCode); } else {clearApplied();}
//     });
//   }
// }
 
$(document).on('pageshow', function(event) {
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then(res => res.json())
      .then(cart => {
        
        const hasBlackFriday =
          Array.isArray(cart.discount_codes) &&
          cart.discount_codes.some(dc => dc.code?.toUpperCase() === 'BLACKFRIDAY');

        if (!hasBlackFriday) return; 

      
        const formData = new FormData();
        formData.append('discount', ''); 

        return fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
      })
      .then(res => {
        if (!res) return;
        return res.json();
      })
      .then(updatedCart => {
        if (!updatedCart) return;
        console.log('Discount removed. New cart:', updatedCart);
        // optional: refresh page or update cart UI here
        location.reload();
      })
      .catch(console.error);
});
 

 
